import { useState } from 'react';
import { AlertCircle, UploadCloud } from 'lucide-react';
import { summarize, useUploads } from './store';
import { UploadQueueSheet } from './UploadQueueSheet';

/**
 * Top-bar mini indicator (§6): visible whenever uploads are live or need attention. Shows a small
 * progress ring with the percentage; tapping opens the queue sheet.
 */
export function UploadIndicator({ businessNames }: { businessNames?: Record<string, string> }) {
  const list = useUploads();
  const s = summarize(list);
  const [open, setOpen] = useState(false);
  if (!s.live && !s.attention && !open) return null;
  const r = 9;
  const c = 2 * Math.PI * r;
  const label = s.live ? `Uploads: ${s.live} in progress, ${s.pct}%` : `Uploads: ${s.attention} need attention`;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        data-testid="upload-indicator"
        className="relative inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-ink-2 hover:bg-surface"
      >
        {s.live ? (
          <svg aria-hidden viewBox="0 0 24 24" className="size-6 -rotate-90">
            <circle cx="12" cy="12" r={r} fill="none" strokeWidth="3" className="stroke-neutral-soft" />
            <circle
              cx="12"
              cy="12"
              r={r}
              fill="none"
              strokeWidth="3"
              strokeLinecap="round"
              className="stroke-primary transition-[stroke-dashoffset] duration-200"
              strokeDasharray={c}
              strokeDashoffset={c * (1 - s.pct / 100)}
            />
          </svg>
        ) : (
          <AlertCircle aria-hidden className="size-5 text-warning" />
        )}
        <span aria-hidden className="tabular-nums">
          {s.live ? `${s.pct}%` : s.attention}
        </span>
        <UploadCloud aria-hidden className="hidden size-4 sm:block" />
      </button>
      <UploadQueueSheet open={open} onClose={() => setOpen(false)} businessNames={businessNames} />
    </>
  );
}
